import { createSelector } from "@reduxjs/toolkit";
import _ from "lodash";
import * as THREE from "three";

import { radToDeg } from "@skydio/math";
import { FlightState } from "@skydio/pbtypes/pbtypes/gen/ambassador/flight_state_pb";
import { convertTransPbToObject3 } from "@skydio/shared_ui/utils/math";

import {
  calculateImuTCamHeadingPitch,
  calculateNavHeadingPitch,
  calculateNavTCommand,
  convertFluToRdf,
} from "./utils";

import type { ValueType } from "@skydio/core";
import type { MovementDirection, PilotState, TeleopAction } from "./types";

export const getClientId = (state: PilotState) => state.vehicleConnection.clientId;

export const getVehicleId = (state: PilotState) => state.vehicleConnection.vehicleId;

export const getMostRecentPilotId = (state: PilotState) => state.mostRecentPilot.clientId;

export const wasCommandeerRequested = (state: PilotState) => state.commandeerRequested;

export const getDirectionForKey = (state: PilotState, targetKey: string) =>
  _.findKey(state.movementKeybindings, key => key === targetKey) as MovementDirection | undefined;

export const getActionForKey = (state: PilotState, targetKey: string) =>
  _.findKey(state.teleopActionKeybindings, key => key === targetKey) as TeleopAction | undefined;

export const getCurrentFlightState = (state: PilotState) => state.flightState;

const createFlightStateMatchSelector = (...flightStates: ValueType<FlightState.EnumMap>[]) =>
  createSelector(getCurrentFlightState, flightState =>
    _.some(flightStates, f => f === flightState)
  );

export const getMaxVehicleTranslationSpeed = (state: PilotState) => state.vehicleSpeedSettings.max;
export const getMinVehicleTranslationSpeed = (state: PilotState) => state.vehicleSpeedSettings.min;
export const getNominalVehicleTranslationSpeed = (state: PilotState) =>
  state.vehicleNominalTranslationSpeed;
export const getIsBoostActive = (state: PilotState) => state.boostModeActive;
export const getIsCrawlActive = (state: PilotState) => state.crawlModeActive;

// account for boost and crawl
// This is the keyboard speed setting that is used for movement
export const getCorrectedNominalVehicleTranslationSpeed = createSelector(
  getNominalVehicleTranslationSpeed,
  getMaxVehicleTranslationSpeed,
  getMinVehicleTranslationSpeed,
  getIsBoostActive,
  getIsCrawlActive,
  (nominalTranslationSpeed, maxTranslationSpeed, minTranslationSpeed, boostActive, crawlActive) => {
    if (boostActive) return maxTranslationSpeed;
    if (crawlActive) return minTranslationSpeed;
    return nominalTranslationSpeed;
  }
);

export const isResting = createFlightStateMatchSelector(FlightState.Enum.REST);
export const isSafetyCheck = createFlightStateMatchSelector(FlightState.Enum.SAFETY_CHECK);
export const isReadyForTakeoff = createFlightStateMatchSelector(
  FlightState.Enum.HAND_TAKEOFF_READY
);
export const isTakingOff = createFlightStateMatchSelector(FlightState.Enum.TAKING_OFF);
export const isLanding = createFlightStateMatchSelector(
  FlightState.Enum.LANDING,
  FlightState.Enum.LANDING_WITH_AVOIDANCE,
  FlightState.Enum.DOCK_LANDING,
  FlightState.Enum.SMART_LAND_PREP,
  FlightState.Enum.PAUSED_FOR_FINAL_DESCENT,
  FlightState.Enum.CASE_LANDING,
  FlightState.Enum.EMERGENCY_LANDING
);
export const isDockLanding = createFlightStateMatchSelector(FlightState.Enum.DOCK_LANDING);
export const isAbortingLanding = createFlightStateMatchSelector(FlightState.Enum.ABORTING_LANDING);
export const isReturningToDock = createFlightStateMatchSelector(FlightState.Enum.RETURNING_TO_DOCK);
export const isReturningToLaunch = createFlightStateMatchSelector(
  FlightState.Enum.RETURNING_TO_LAUNCH
);
export const isReturningToSafeLanding = createFlightStateMatchSelector(
  FlightState.Enum.RETURNING_TO_SAFE_LANDING
);
export const isReturningToPoint = createFlightStateMatchSelector(
  FlightState.Enum.FLYING_SUBJECT_RTP
);
export const isInFlight = createSelector(
  getCurrentFlightState,
  // NOTE(sam): maybe worth implementing more nuanced logic here but for now we can rely on
  // the enum values for all potential in-flight statuses being between 200 and 300
  (flightState = FlightState.Enum.INVALID) =>
    _.inRange(flightState, FlightState.Enum.FLYING_UNKNOWN, FlightState.Enum.TAKING_OFF)
);
export const getIsMostRecentVehiclePilot = createSelector(
  [getClientId, getMostRecentPilotId],
  (clientId, pilotId) => {
    return clientId === pilotId && clientId !== "";
  }
);
export const isInPostFlight = createFlightStateMatchSelector(FlightState.Enum.POST_FLIGHT);
export const isInFlyingUnknownState = createFlightStateMatchSelector(
  FlightState.Enum.FLYING_UNKNOWN
);
export const isLandedInDockAndDockIsClosing = createFlightStateMatchSelector(
  FlightState.Enum.ARMED_IN_DOCK_POST_FLIGHT
);
/**
 * Returns true if the vehicle is in a state where it is waiting for takeoff. Includes rest phase
 */
export const isWaitingForTakeoff = createSelector(getCurrentFlightState, flightState => {
  return (
    flightState &&
    flightState >= FlightState.Enum.REST &&
    flightState < FlightState.Enum.POST_FLIGHT
  );
});

export const getImuTCamEOPb = (state: PilotState) => state.eo_imu_T_cam;
export const getImuTCamIRPb = (state: PilotState) => state.ir_imu_T_cam;
export const getImuTCamIR = createSelector(getImuTCamIRPb, imuTCamIR => {
  if (imuTCamIR) {
    return convertTransPbToObject3(imuTCamIR);
  }
  // identity as a fallback
  return new THREE.Object3D();
});

const getGimbalCamRImu = (state: PilotState) => state.gimbal_cam_R_imu;
export const getImuRGimbalCamRdf = createSelector(getGimbalCamRImu, gimbalCamRImu =>
  gimbalCamRImu ? convertFluToRdf(gimbalCamRImu.clone().invert()) : undefined
);

export const getImuTCamPb = (state: PilotState) => state.imu_T_subject_cam;

export const getImuTCamEO = createSelector(getImuTCamPb, getImuTCamEOPb, (imuTCam, imuTCamEO) => {
  if (imuTCamEO) {
    return convertTransPbToObject3(imuTCamEO);
  } else if (imuTCam) {
    return convertTransPbToObject3(imuTCam);
  }
  // identity as a fallback
  return new THREE.Object3D();
});

export const getNavTGimbalCamImuPb = (state: PilotState) =>
  state.nav_T_gimbal_camera_imu?.transform;
export const getNavTGimbalCamImu = createSelector(getNavTGimbalCamImuPb, navTGimbalCamImu =>
  navTGimbalCamImu ? convertTransPbToObject3(navTGimbalCamImu) : undefined
);

// If nav_t_cam is stale, position commands will be referenced of an old vehicle pose and so will be
// inaccurate. This resolves SW-49492. Reduced from 5000 to 1000 following CS-11785.
const TRANSFORM_STALE_THRESHOLD = 1000; // ms
const isNavTGimbalCamImuStale = (state: PilotState) =>
  state.nav_T_gimbal_camera_imu
    ? performance.now() - state.nav_T_gimbal_camera_imu.timestamp > TRANSFORM_STALE_THRESHOLD
    : false;

export const getNavTCommand = createSelector(
  getNavTGimbalCamImu,
  getImuTCamEO,
  getImuRGimbalCamRdf,
  isNavTGimbalCamImuStale,
  calculateNavTCommand
);

export const getVehicleOrientation = (state: PilotState) => state.vehicleOrientation;

export const getCamNavHeadingPitch = createSelector(
  getNavTCommand,
  getVehicleOrientation,
  calculateNavHeadingPitch
);
export const getGimbalImuNavHeadingPitch = createSelector(
  getNavTGimbalCamImu,
  getVehicleOrientation,
  calculateNavHeadingPitch
);
export const getGimbalImuNavHeadingPitchDisplay = createSelector(
  getGimbalImuNavHeadingPitch,
  navHeadingPitch => {
    // convert rad to deg and reverse the direction so that positive pitch is up
    return Math.round(radToDeg(navHeadingPitch?.pitch ?? 0)) * -1;
  }
);

export const getGimbalImuToCamHeadingPitch = createSelector(
  getImuTCamEO,
  calculateImuTCamHeadingPitch
);

// NOTE (divyanshu): extracting individual components to force the selector to listen to changes in
// the Vector3 objects in state.pilot.vehiclePosition and state.pilot.vehicleVelocity
const vehiclePosition = (state: PilotState) =>
  new THREE.Vector3(state.vehiclePosition?.x, state.vehiclePosition?.y, state.vehiclePosition?.z);
export const getVehiclePosition = createSelector(vehiclePosition, position => position);

export const isInPerch = createFlightStateMatchSelector(FlightState.Enum.PERCH);
